import { defaultErrorHandler, handle } from '@/errorHandling';
import {
  AccountApi,
  CommentsApi,
  Configuration,
  DataSetsApi,
  EntityLockTokensApi,
  EnumerationQuantitiesApi,
  FilesApi,
  ImagesApi,
  InstallApi,
  JobConfigurationsApi,
  JobOccurrencesApi,
  LicensesApi,
  LocalizationApi,
  MeasurementDataApi,
  MedicalSystemsApi,
  NumericQuantitiesApi,
  ProtocolsApi,
  PtwEdgeUpdateApi,
  QaEquipmentApi,
  QaEquipmentCloudApi,
  RolesApi,
  SystemApi,
  TaskItemsApi,
  TrackItImportApi,
  TenantsApi,
  TextsApi,
  UnitsApi,
  UsersApi,
  ResultsApi,
  BccMeasurementsApi,
  UnicheckSimulatorApi,
  ReportsApi,
  BaseAPI,
  ConfigurationParameters,
  TokenAuthApi,
} from './generator';
import { MODE } from '@/env';
import { type Mode } from '@/@types/env';
import { TokenAuth } from '@/authentication/tokenAuth';
import { hmrSafeSingleton } from '@/utils/hmr';

export const auth = hmrSafeSingleton('__TOKEN_AUTH__', new TokenAuth());

const apiModeConfigurations: Partial<Record<Mode, ConfigurationParameters>> = {
  test: {
    middleware: [auth.getFetchMiddleware()],
    handleFunction(promise, errorHandlers = []) {
      return handle(promise, [...errorHandlers]);
    },
  },
};

const commonApiConfiguration = new Configuration({
  basePath: window.location.origin, // we need a `basePath` to make vite-proxy work
  middleware: [auth.getFetchMiddleware()],
  handleFunction(promise, errorHandlers = []) {
    return handle(promise, [...errorHandlers, defaultErrorHandler]);
  },
  ...(apiModeConfigurations[MODE] || {}),
});

export const api = {
  base: new BaseAPI(commonApiConfiguration),
  account: new AccountApi(commonApiConfiguration),
  dataSetApi: new DataSetsApi(commonApiConfiguration),
  localizationApi: new LocalizationApi(commonApiConfiguration),
  usersApi: new UsersApi(commonApiConfiguration),
  rolesApi: new RolesApi(commonApiConfiguration),
  qaEquipmentApi: new QaEquipmentApi(commonApiConfiguration),
  qaEquipmentCloudApi: new QaEquipmentCloudApi(commonApiConfiguration),
  medicalSystemsApi: new MedicalSystemsApi(commonApiConfiguration),
  jobOccurrencesApi: new JobOccurrencesApi(commonApiConfiguration),
  jobConfigurationsApi: new JobConfigurationsApi(commonApiConfiguration),
  tenantsApi: new TenantsApi(commonApiConfiguration),
  installApi: new InstallApi(commonApiConfiguration),
  unitsApi: new UnitsApi(commonApiConfiguration),
  numericQuantitiesApi: new NumericQuantitiesApi(commonApiConfiguration),
  enumerationQuantitiesApi: new EnumerationQuantitiesApi(commonApiConfiguration),
  textsApi: new TextsApi(commonApiConfiguration),
  systemApi: new SystemApi(commonApiConfiguration),
  filesApi: new FilesApi(commonApiConfiguration),
  entityLockTokensApi: new EntityLockTokensApi(commonApiConfiguration),
  imagesApi: new ImagesApi(commonApiConfiguration),
  commentsApi: new CommentsApi(commonApiConfiguration),
  measurementDataApi: new MeasurementDataApi(commonApiConfiguration),
  licenseApi: new LicensesApi(commonApiConfiguration),
  protocolsApi: new ProtocolsApi(commonApiConfiguration),
  taskItemsApi: new TaskItemsApi(commonApiConfiguration),
  trackItImportApi: new TrackItImportApi(commonApiConfiguration),
  ptwEdgeUpdateApi: new PtwEdgeUpdateApi(commonApiConfiguration),
  resultsApi: new ResultsApi(commonApiConfiguration),
  bccMeasurementsApi: new BccMeasurementsApi(commonApiConfiguration),
  unicheckSimulatorApi: new UnicheckSimulatorApi(commonApiConfiguration),
  reportsApi: new ReportsApi(commonApiConfiguration),
  tokenAuthApi: new TokenAuthApi(commonApiConfiguration),
};
